// biome-ignore-all lint/suspicious/noArrayIndexKey: the portal-notes preview renders a newest-first list in a stable order that is never reordered mid-render.
// =============================================================================
// ShelterDetail.jsx — full-screen, Unleashed-style tabbed shelter detail
// =============================================================================
// Phase 1 of the shelter-admin tabbed redesign (5 July 2026 —
// docs/shelter-admin-tabbed-redesign-plan.md + docs/handover-2026-07-05-shelter-admin-plan.md).
//
// Rendered by UserAdmin (AdminPages.jsx) when the URL is /users/:shelterCode —
// it REPLACES the whole Shelters list screen, like Unleashed's customer record.
// Four tabs (Details / Address / Users / Delivery) share ONE editable form with
// a single Save; Customer Code is read-only (it ties requests, imports and
// user_shelters together).
//
// The component OWNS its form state + save (updateShelterById with the db-40
// columns abn / phone / physical_*), but orchestration stays in UserAdmin:
// deactivate/reactivate handlers, the Manage-Addresses modal and the
// memberships/attached-users data are passed in as props.
//
// Users tab: attached-users list (incl. login-less contacts) + the PRIMARY CONTACT
// selector — points shelters.primary_contact at the chosen membership via
// setPrimaryContactMembership (contact-model pointer-only; the SHELTER axis — NOT the
// user-axis is_primary that drives the session default). Works for a login-less
// contact too (keyed by membership id). Setting a primary is a single atomic action
// (one click = one committed change), so it writes immediately, per the
// ephemeral-staging principle's exception.
// =============================================================================

import React from "react";
import { useNavigate } from "react-router-dom";
import { canAccess, isAdminRole } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { getBankDetails, upsertBankDetails } from "../../data/shelterBankDetails";
import { listShelterChangeLog } from "../../data/shelterChangeLog";
import { getShelterNotes, upsertShelterNotes } from "../../data/shelterNotes";
import { getShelterByIdFull, updateShelterById } from "../../data/shelters";
import { getServiceDocSignedUrl, listServiceDocs, uploadServiceDoc } from "../../data/storage";
import {
  enqueueContactPushForMembership,
  enqueueUnleashedPush,
} from "../../data/unleashedPushQueue";
import { setPrimaryContactMembership } from "../../data/userShelters";
import { confirmDialog } from "../../services/dialogService";
import { fetchLoginActivation, hasLoggedIn } from "../../services/loginActivation";
import { confirmNavAway, setNavGuard } from "../../services/navGuard";
import { parsePortalNotes, prependNote } from "../../services/portalNotes";
import { computeShelterChanges } from "../../services/shelterChangeSummary";
import { missingRequiredShelterFields } from "../../services/shelterValidation";
import { logAudit } from "../../utils/auditLog";
import { GLYPH } from "../../utils/glyphs";
import { logger } from "../../utils/logger";
import { formatPhone } from "../../utils/sanitize";
import { Btn } from "../../utils/ui.jsx";
import { AddressAutocomplete } from "../form/AddressAutocomplete.jsx";
import { ModalBackdrop, Toggle } from "../form/FormUI.jsx";
import { AttachUserModal } from "./AttachUserModal.jsx";
import { CaseworkerEditForm } from "./CaseworkerEditForm.jsx";
import { DeliveryAddressEditModal } from "./DeliveryAddressEditModal.jsx";
import { DeliveryAddressRows } from "./DeliveryAddressRows.jsx";
import { PreparedTopUpsPanel } from "./PreparedTopUpsPanel.jsx";
import { RaiseTopUpModal } from "./RaiseTopUpModal.jsx";
import { ShelterPortalNotesModal } from "./ShelterPortalNotesModal.jsx";
import { SisterServicesStrip } from "./SisterServicesStrip.jsx";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

// Tab labels per John's review (5 July 2026).
const TABS = [
  ["details", "Details"],
  ["address", "DFV Service Address"],
  ["users", "User/Caseworker"],
  ["delivery", "Delivery Addresses"],
];

// Seed the single editable form from a shelters row. Exported for tests.
export function seedShelterForm(s) {
  return {
    shelter_name: s.shelter_name || "",
    state: s.state || "",
    customer_type: s.customer_type || "",
    sales_group: s.sales_group || "",
    default_warehouse: s.default_warehouse || "", // db-43 → db-58: stores the Unleashed warehouse CODE (dropdown shows the name)
    // Same default as the old edit form: unset (null) counts as taxable.
    taxable: s.taxable === true || s.taxable === null,
    // db-127: Stop Credit — a flag the portal only displays/pushes to Unleashed
    // (it gates nothing here). Defaults false.
    stop_credit: s.stop_credit === true,
    // db-153: portal-only "Stock Held" flag (NOT imported / NOT synced to Unleashed).
    stock_held: s.stock_held === true,
    abn: s.abn || "",
    date_onboarded: s.date_onboarded || "",
    // Contact-model Phase 3 (Stage 1 de-wire): the shelter's email/phone are NOT
    // held on the form — they derive read-only from the Primary contact (Details
    // tab, storedPrimary). The legacy shelters.email/phone columns drop in Stage 2.
    unleashed_guid: s.unleashed_guid || "",
    // Phase 2 (db-42): free-text Reminder — the school → uniform-colour list
    // Unleashed shows on a customer. Admin-only display for now.
    reminder: s.reminder || "",
    // internal_notes = Unleashed's Notes: imported once, READ-ONLY (shown in the
    // "Unleashed Notes" modal, carried on the Customer push). Kept on the form only for
    // that read-only display; NOT written back by Save.
    internal_notes: s.internal_notes || "",
    // db-130: the portal's OWN editable note-taking field (telemarketing + general).
    dfv_service_portal_notes: s.dfv_service_portal_notes || "",
    postal_line1: s.postal_line1 || "",
    postal_line2: s.postal_line2 || "",
    postal_suburb: s.postal_suburb || "",
    postal_suburb_city: s.postal_suburb_city || "",
    postal_state: s.postal_state || "",
    postal_postcode: s.postal_postcode || "",
    physical_line1: s.physical_line1 || "",
    physical_line2: s.physical_line2 || "",
    physical_suburb: s.physical_suburb || "",
    physical_suburb_city: s.physical_suburb_city || "",
    physical_state: s.physical_state || "",
    physical_postcode: s.physical_postcode || "",
  };
}

// The Save patch — every editable field across the tabs (NO shelter_code: it is
// read-only on the detail). Exported for tests.
export function buildShelterPatch(form, byEmail) {
  return {
    shelter_name: form.shelter_name,
    state: form.state,
    customer_type: form.customer_type,
    sales_group: form.sales_group,
    default_warehouse: form.default_warehouse,
    taxable: form.taxable !== false,
    stop_credit: form.stop_credit === true,
    stock_held: form.stock_held === true,
    abn: form.abn,
    date_onboarded: form.date_onboarded || null,
    // Contact-model Phase 3: shelters.email/phone are derived from the Primary
    // contact (read-only on the Details tab) and drop in Phase 3 — no longer written.
    // unleashed_guid is sync-owned (set by Link Customers) and read-only here — the
    // Details Save no longer writes it, so it can't overwrite a fresher synced value.
    // reminder + internal_notes (Unleashed's Notes) are NOT written from the Details form —
    // both are read-only (Unleashed → portal, synced on the Customer push). The portal's
    // own notes (dfv_service_portal_notes, db-130) are ALSO not written here: they're a
    // list managed by the "+ Note" modal, which writes each note immediately (atomic), so
    // the form Save must not round-trip (and possibly clobber) them.
    postal_line1: form.postal_line1,
    postal_line2: form.postal_line2,
    postal_suburb: form.postal_suburb,
    postal_suburb_city: form.postal_suburb_city,
    postal_state: form.postal_state,
    postal_postcode: form.postal_postcode,
    physical_line1: form.physical_line1,
    physical_line2: form.physical_line2,
    physical_suburb: form.physical_suburb,
    physical_suburb_city: form.physical_suburb_city,
    physical_state: form.physical_state,
    physical_postcode: form.physical_postcode,
    updated_by: byEmail || "",
  };
}

// Render a change-log value for the History modal. null/blank → em dash; booleans →
// Yes/No; everything else stringified.
function fmtChangeValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

// db-128: only PDFs may be uploaded to a DFV Service. True for an application/pdf
// MIME type or a .pdf extension. Exported for the unit test.
export function isPdfFile(file) {
  if (!file) return false;
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
}

// Strip the "<timestamp>-" upload prefix from a stored doc name for display.
function docDisplayName(name) {
  return String(name || "").replace(/^\d+-/, "");
}

const labelCls = "text-[11px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1";
const inputCls =
  "w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

function Field({ id, label, value, onChange, type, placeholder, hint, req }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
        {req && <span className="text-zephyr-red">&nbsp;*</span>}
        {hint && <span className="normal-case font-normal text-zephyr-muted"> {hint}</span>}
      </label>
      <input
        id={id}
        type={type || "text"}
        value={value}
        placeholder={placeholder || ""}
        onChange={function (e) {
          onChange(e.target.value);
        }}
        className={inputCls}
        style={{ fontFamily: "inherit" }}
      />
    </div>
  );
}

function SelectField({ id, label, value, onChange, options, allowEmpty, req }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
        {req && <span className="text-zephyr-red">&nbsp;*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={function (e) {
          onChange(e.target.value);
        }}
        className={inputCls}
        style={{ fontFamily: "inherit" }}
      >
        {allowEmpty && <option value="">— Select —</option>}
        {/* Keep the current value selectable even if it's no longer in the option
            list (e.g. a deactivated warehouse code) — otherwise the field would
            silently blank on the next save. */}
        {value && !options.some((o) => (typeof o === "string" ? o : o.value) === value) && (
          <option value={value}>{value}</option>
        )}
        {options.map(function (o) {
          // Options may be plain strings (value === label) OR { value, label }
          // objects — the warehouse dropdown stores a code but shows the name.
          const val = typeof o === "string" ? o : o.value;
          const lbl = typeof o === "string" ? o : o.label;
          return (
            <option key={val} value={val}>
              {lbl}
            </option>
          );
        })}
      </select>
    </div>
  );
}

// One postal/physical address block (6 fields). `prefix` = "postal" | "physical".
// 2-col inner grid: the two blocks sit side by side (Postal | Physical) on the
// Address tab (John, 5 July 2026), so each has half the width.
function AddressBlock({ prefix, form, setF }) {
  const get = (k) => form[`${prefix}_${k}`] || "";
  // Google address autocomplete only on the PHYSICAL address — the postal address
  // is a mailing address (often a PO Box), which Places autocomplete doesn't return,
  // so Line 1 there is a plain input.
  const useAutocomplete = prefix === "physical";
  return (
    <div className="grid grid-cols-2 gap-3">
      <div style={{ gridColumn: "1 / -1" }}>
        {useAutocomplete ? (
          <>
            {/* Picking a suggestion fills Line 1 + Suburb/City + State + Postcode;
                typing still works. Inside the read-only <fieldset disabled> the input
                is disabled so no dropdown fires until Edit. */}
            <label htmlFor={`sd-${prefix}-line1`} className={labelCls}>
              Address Line 1
            </label>
            <AddressAutocomplete
              inputId={`sd-${prefix}-line1`}
              value={get("line1")}
              onChange={(v) => setF(`${prefix}_line1`, v)}
              onSelect={(parts) => {
                setF(`${prefix}_line1`, parts.street || "");
                if (parts.street2) setF(`${prefix}_line2`, parts.street2);
                setF(`${prefix}_suburb_city`, parts.suburb || "");
                setF(`${prefix}_state`, parts.state || "");
                setF(`${prefix}_postcode`, parts.postcode || "");
              }}
              placeholder="Start typing an address…"
              inputStyle={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--color-zephyr-border)",
                fontSize: 13,
                color: "var(--color-zephyr-text)",
                background: "white",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
          </>
        ) : (
          <Field
            id={`sd-${prefix}-line1`}
            label="Address Line 1 / PO Box"
            value={get("line1")}
            onChange={(v) => setF(`${prefix}_line1`, v)}
            placeholder="e.g. PO Box 123"
          />
        )}
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <Field
          id={`sd-${prefix}-line2`}
          label="Address Line 2"
          value={get("line2")}
          onChange={(v) => setF(`${prefix}_line2`, v)}
        />
      </div>
      <Field
        id={`sd-${prefix}-suburb-city`}
        label="Suburb/City"
        value={get("suburb_city")}
        onChange={(v) => setF(`${prefix}_suburb_city`, v)}
      />
      <Field
        id={`sd-${prefix}-state`}
        label="State / Region"
        value={get("state")}
        onChange={(v) => setF(`${prefix}_state`, v)}
      />
      <Field
        id={`sd-${prefix}-postcode`}
        label="Postal Code"
        value={get("postcode")}
        onChange={(v) => setF(`${prefix}_postcode`, v)}
      />
    </div>
  );
}

export function ShelterDetail({
  shelter,
  customerTypes,
  salesGroups,
  warehouses, // managed Default Warehouse options (lookup_options category 'warehouse')
  attachedUsers, // FULL profile rows attached to THIS shelter (Phase 3: startEdit needs every profile field, not the AttachedUser summary)
  memberships, // ALL user_shelters rows (id, user_id, shelter_code, …); id marks the Primary vs shelter.primary_contact
  onBack,
  onDeactivateShelter,
  onReactivateShelter,
  openDeliveryModal,
  // Delivery-address management (moved inline onto the tab, 8 Aug — the old "Manage
  // Addresses" button + DeliveryAddressesModal are retired). All state + handlers are
  // owned by UserAdmin/useUserAdminHandlers; this tab drives the context via
  // openDeliveryModal (loads the FULL list incl. deactivated) and renders inline.
  deliveryAddrs,
  deliveryLoading,
  editingAddr,
  setEditingAddr,
  addrForm,
  setAddrForm,
  deliverySaving,
  saveAddr,
  startNewAddr,
  startEditAddr,
  // Users-tab management bag (Phase 3): { editing, form, setForm, fv, setF,
  // saving, saveEdit, startEdit, cancelEdit, sendPasswordReset,
  // setConfirmDeactivate, shelters, caseworkers, onNewUser, onAttachUser,
  // onDetachUser }. UserAdmin owns the state + handlers (the intentional
  // handler-factory pattern); this component only renders them.
  usersAdmin,
  onSaved, // (msg) => parent showSaved + loadUsers
  onMembershipsChanged, // () => parent loadUsers (after a Primary change)
  // "admin" (default) | "telemarketing". In the telemarketing call modal we hide
  // View Requests + Deactivate + the Unleashed GUID (telemarketers otherwise have
  // supervisor rights). ShelterEditor passes this through; AdminPages omits it → admin.
  context = "admin",
}) {
  const { user } = useSession();
  const isTelemarketing = context === "telemarketing";
  const navigate = useNavigate();
  const [form, setForm] = React.useState(() => seedShelterForm(shelter));
  const [activeTab, setActiveTab] = React.useState("details");
  // Explicit Edit mode (John, 20 Jul 2026): the Details + DFV Service Address tabs
  // are read-only until "Edit" is clicked; the footer then shows Save + Cancel.
  const [editing, setEditing] = React.useState(false);
  // Snapshot taken when Edit is clicked, so Cancel (and a discarded tab switch)
  // can revert exactly to the pre-edit state.
  const editBaselineRef = React.useRef(null);
  const [saving, setSaving] = React.useState(false);
  const [saveErr, setSaveErr] = React.useState("");
  const [primarySaving, setPrimarySaving] = React.useState(false); // Save in flight
  // Primary contact is now a STAGED edit (John, 22 Jul): read-only until "Change
  // Primary" is clicked; the radios then pick a draft, and Save writes it once. This
  // replaces the old click-to-write-instantly + optimistic-copy approach (which went
  // stale after a user edit re-created the membership rows and showed "no Primary").
  const [primaryEditing, setPrimaryEditing] = React.useState(false);
  const [draftPrimary, setDraftPrimary] = React.useState(null);
  // Phase 3: the searchable "Attach existing user" picker (a flat select
  // doesn't scale to hundreds of caseworkers — John, 5 July 2026).
  const [showAttachModal, setShowAttachModal] = React.useState(false);
  // "Show Deactivated Contacts" filter on the Users tab (default OFF — John, screen 1).
  const [showDeactivatedContacts, setShowDeactivatedContacts] = React.useState(false);
  // Login-activation: user_id -> last_sign_in_at (or null). Drives the "Live" badge
  // in the STATUS column so we can see which logins have actually been used. A login
  // user's auth id === its profile id (u.id). Keyed on the sorted id set so it only
  // refetches when the set of login users actually changes, not on every render.
  const [loginActivation, setLoginActivation] = React.useState({});
  const loginUserIdKey = (attachedUsers || [])
    .filter((u) => u && !u.noLogin && u.id)
    .map((u) => u.id)
    .sort()
    .join(",");
  React.useEffect(
    function () {
      if (!loginUserIdKey) {
        setLoginActivation({});
        return undefined;
      }
      let alive = true;
      fetchLoginActivation(loginUserIdKey.split(",")).then(function (m) {
        if (alive) setLoginActivation(m);
      });
      return function () {
        alive = false;
      };
    },
    [loginUserIdKey]
  );
  // db-127: Bank details live in the SEPARATE admin-only shelter_bank_details table
  // (kept off `form`, which maps to `shelters`). Loaded on open, saved alongside the
  // shelters patch. savedBankRef mirrors savedFormRef for the unsaved-changes guard.
  const [bankForm, setBankForm] = React.useState({ bank_name: "", bank_account: "" });
  const savedBankRef = React.useRef({ bank_name: "", bank_account: "" });
  // db-127: the change-history modal (button → modal; reads shelter_change_log).
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  // db-128: admin-only PDF documents for this service (button → modal; no tracking,
  // no remove — upload + open only, per John).
  const [unleashedNotesOpen, setUnleashedNotesOpen] = React.useState(false);
  // Portal notes (db-130 dfv_service_portal_notes) — an add-a-note modal (list newest
  // first). Notes are written immediately (atomic, like a comment), INDEPENDENT of the
  // edit-gated Details form: their own state, and NOT part of buildShelterPatch, so the
  // form Save can never overwrite them and the nav-guard never flags them.
  const [notesOpen, setNotesOpen] = React.useState(false);
  const [notesBusy, setNotesBusy] = React.useState(false);
  const [portalNotesRaw, setPortalNotesRaw] = React.useState(
    () => shelter.dfv_service_portal_notes || ""
  );
  React.useEffect(
    function () {
      setPortalNotesRaw(shelter.dfv_service_portal_notes || "");
    },
    [shelter.id, shelter.dfv_service_portal_notes]
  );
  // Load the DFV Service Notes (shelter_notes) for THIS service when it opens.
  React.useEffect(
    function () {
      let alive = true;
      const key = shelter.shelter_code || shelter.shelter_name;
      if (!key) return undefined;
      const field = shelter.shelter_code ? "shelter_code" : "shelter_name";
      getShelterNotes(field, key).then(function (row) {
        if (!alive) return;
        const n = row?.notes || "";
        const na = row?.no_approvals || false;
        setServiceNotes(n);
        setNoApprovals(na);
        savedNotesRef.current = { notes: n, noApprovals: na };
      });
      return function () {
        alive = false;
      };
    },
    [shelter.id, shelter.shelter_code, shelter.shelter_name]
  );
  const [docsOpen, setDocsOpen] = React.useState(false);
  const [docs, setDocs] = React.useState([]);
  const [docsLoading, setDocsLoading] = React.useState(false);
  // Load the document count when the service opens, so the Documents button can
  // show it without opening the modal (db-128). The modal reload refreshes it.
  React.useEffect(
    function () {
      let alive = true;
      listServiceDocs(shelter.id).then(function (rows) {
        if (alive) setDocs(rows || []);
      });
      return function () {
        alive = false;
      };
    },
    [shelter.id]
  );
  const [docUploading, setDocUploading] = React.useState(false);
  const [docErr, setDocErr] = React.useState("");
  // Phase 5 — staff "Raise Stock Top-up": prepare a top-up FOR this service (writes a
  // pending stock_top_up_prefill row the service later submits read-only). Shown in the
  // telemarketing call view (the primary use case) AND to anyone who can reach the DFV
  // Services editor (has DFV Services "users" access in Manage). NOT gated by the
  // stock_top_up_enabled switch: that switch governs the SHELTER's own self-serve chooser,
  // whereas staff-prepare is a separate capability — and the prefill path already lets the
  // service submit a staff-prepared top-up regardless of the switch (John, 13 Aug).
  const canRaiseTopUp = isTelemarketing || canAccess(user, "users");
  const [raiseTopUpOpen, setRaiseTopUpOpen] = React.useState(false);
  // Bumped after a top-up is prepared so the Prepared top-ups panel reloads (19 Oct 2026).
  const [prefillReload, setPrefillReload] = React.useState(0);
  // DFV Service Notes (the `shelter_notes` table — the responder-facing note + the
  // "No requests to approve — refer to management" flag). Surfaced on the Details tab
  // BEHIND EDIT and saved as part of the ONE Save (John, 13 Aug 2026). ⚠️ NOT synced to
  // Unleashed — shelter_notes is a portal-internal table, never in customerMap/pushCustomer.
  const [serviceNotes, setServiceNotes] = React.useState("");
  const [noApprovals, setNoApprovals] = React.useState(false);
  const savedNotesRef = React.useRef({ notes: "", noApprovals: false });
  const serviceNotesRef = React.useRef("");
  serviceNotesRef.current = serviceNotes;
  const noApprovalsRef = React.useRef(false);
  noApprovalsRef.current = noApprovals;

  // Re-seed only when a DIFFERENT shelter is opened (not on parent refreshes of
  // the same row — that would clobber unsaved edits).
  const seededIdRef = React.useRef(shelter.id);
  React.useEffect(() => {
    if (seededIdRef.current !== shelter.id) {
      seededIdRef.current = shelter.id;
      setForm(seedShelterForm(shelter));
      setActiveTab("details");
      setPrimaryEditing(false);
      setDraftPrimary(null);
      setSaveErr("");
      savedFormRef.current = null;
    }
  }, [shelter]);

  // Pull the freshest shelter from the DB when a service is opened, so the Details /
  // DFV Service Address tabs never show a stale admin-list cache (e.g. right after an
  // Unleashed import ran on another screen — John, 2 Aug 2026). Runs per service
  // opened; editing is false on open, so it can't clobber an in-progress edit.
  React.useEffect(() => {
    let cancelled = false;
    getShelterByIdFull(shelter.id).then(function (fresh) {
      if (cancelled || !fresh || fresh.id !== shelter.id) return;
      const seeded = seedShelterForm(fresh);
      setForm(seeded);
      savedFormRef.current = JSON.stringify(seeded);
      // Keep the independent portal-notes state in step with the fresh fetch too.
      setPortalNotesRaw(fresh.dfv_service_portal_notes || "");
    });
    return function () {
      cancelled = true;
    };
  }, [shelter.id]);

  // ── Unsaved-changes guard (John, 5 July 2026) ──────────────────────────────
  // Dirty = the form differs from its last-saved state (the seed, or the last
  // successful Save — savedFormRef bridges the moment between a Save and the
  // parent's background refresh landing). Covers Header nav + Sign Out via
  // navGuard, browser refresh/close via beforeunload, and the "← Back to
  // Shelters" link below. Users-tab actions save immediately and are never
  // "unsaved". Browser Back is NOT intercepted (accepted, same as elsewhere).
  const savedFormRef = React.useRef(null);
  const formRef = React.useRef(form);
  formRef.current = form;
  const bankFormRef = React.useRef(bankForm);
  bankFormRef.current = bankForm;
  const shelterRef = React.useRef(shelter);
  shelterRef.current = shelter;
  const primaryEditingRef = React.useRef(false);
  primaryEditingRef.current = primaryEditing;
  const guardRef = React.useRef(null);
  guardRef.current = function () {
    if (primaryEditingRef.current)
      return "You have an unsaved Primary contact change. Leave and discard it?";
    if (JSON.stringify(bankFormRef.current) !== JSON.stringify(savedBankRef.current))
      return "You have unsaved DFV Service changes (Details / DFV Service Address). Leave and discard them?";
    if (
      serviceNotesRef.current !== savedNotesRef.current.notes ||
      noApprovalsRef.current !== savedNotesRef.current.noApprovals
    )
      return "You have unsaved DFV Service Notes. Leave and discard them?";
    const baseline = savedFormRef.current ?? JSON.stringify(seedShelterForm(shelterRef.current));
    return JSON.stringify(formRef.current) !== baseline
      ? "You have unsaved DFV Service changes (Details / DFV Service Address). Leave and discard them?"
      : null;
  };
  React.useEffect(() => {
    const guard = function () {
      return guardRef.current ? guardRef.current() : null;
    };
    const unregister = setNavGuard(guard);
    const onBeforeUnload = function (e) {
      if (guard()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return function () {
      unregister();
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  async function handleBack() {
    if (!(await confirmNavAway())) return;
    onBack();
  }

  // Delivery management is now inline on the tab. When the Delivery tab is opened
  // (or a different shelter loads), load the FULL address list into the shared
  // delivery context (openDeliveryModal → listAllByShelterId, incl. deactivated) so
  // the handlers (Ban/Silent/Promote/…) — which reload via that same context — keep
  // the inline list fresh. Deliberately keyed on [activeTab, shelter.id] only
  // (openDeliveryModal is an unstable handler-factory fn, intentionally excluded).
  React.useEffect(() => {
    if (activeTab !== "delivery" || !shelter.id) return;
    openDeliveryModal(shelter.id, shelter.shelter_code, shelter.shelter_name);
  }, [activeTab, shelter.id]);

  // db-127: load the admin-only bank details for this shelter on open. Non-admins
  // get null (RLS) → blank fields. savedBankRef anchors the unsaved-changes guard.
  React.useEffect(() => {
    if (!shelter.id) return;
    getBankDetails(shelter.id).then(function (row) {
      const b = { bank_name: row?.bank_name || "", bank_account: row?.bank_account || "" };
      setBankForm(b);
      savedBankRef.current = b;
    });
  }, [shelter.id]);

  function setF(key, val) {
    setForm(function (f) {
      return Object.assign({}, f, { [key]: val });
    });
  }

  function copyPhysicalToPostal() {
    setForm(function (f) {
      return Object.assign({}, f, {
        postal_line1: f.physical_line1,
        postal_line2: f.physical_line2,
        postal_suburb: f.physical_suburb,
        postal_suburb_city: f.physical_suburb_city,
        postal_state: f.physical_state,
        postal_postcode: f.physical_postcode,
      });
    });
  }

  function startEdit() {
    editBaselineRef.current = form;
    setSaveErr("");
    setEditing(true);
  }

  function cancelEdit() {
    if (editBaselineRef.current) setForm(editBaselineRef.current);
    setBankForm(savedBankRef.current); // revert bank edits to the last saved/loaded
    setServiceNotes(savedNotesRef.current.notes); // revert DFV Service Notes edits
    setNoApprovals(savedNotesRef.current.noApprovals);
    setSaveErr("");
    setEditing(false);
  }

  async function openHistory() {
    setHistoryOpen(true);
    setHistoryLoading(true);
    const rows = await listShelterChangeLog(shelter.id);
    setHistory(rows);
    setHistoryLoading(false);
  }

  async function reloadDocs() {
    setDocsLoading(true);
    setDocs(await listServiceDocs(shelter.id));
    setDocsLoading(false);
  }
  async function openDocs() {
    setDocErr("");
    setDocsOpen(true);
    await reloadDocs();
  }
  async function onUploadDoc(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file after an error
    if (!file) return;
    if (!isPdfFile(file)) {
      setDocErr("Only PDF files can be uploaded.");
      return;
    }
    setDocErr("");
    setDocUploading(true);
    const { error } = await uploadServiceDoc(shelter.id, file);
    setDocUploading(false);
    if (error) {
      setDocErr("Upload failed — please try again.");
      return;
    }
    await reloadDocs();
  }
  async function openDoc(path) {
    const url = await getServiceDocSignedUrl(path);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else setDocErr("Could not open the file.");
  }

  // Guarded tab switch: leaving Details/Address mid-edit with unsaved changes
  // prompts to discard (John — "warned moving tabs if you haven't saved the work
  // on the tab you are leaving"). Users/Delivery actions save instantly, so a
  // switch involving them only guards the outgoing Details/Address edit.
  async function requestTab(tab) {
    if (tab === activeTab) return;
    if (primaryEditing) {
      const ok = await confirmDialog({
        title: "Unsaved Primary change",
        message: "You haven't saved the Primary contact change. Discard it and switch?",
        confirmLabel: "Discard & switch",
        cancelLabel: "Stay",
        danger: true,
      });
      if (!ok) return;
      cancelPrimaryEdit();
    }
    if (editing) {
      const dirty = guardRef.current ? guardRef.current() : null;
      if (dirty) {
        const ok = await confirmDialog({
          title: "Unsaved changes",
          message: "You have unsaved changes on this tab. Discard them and switch?",
          confirmLabel: "Discard & switch",
          cancelLabel: "Stay",
          danger: true,
        });
        if (!ok) return;
        if (editBaselineRef.current) setForm(editBaselineRef.current);
        setServiceNotes(savedNotesRef.current.notes);
        setNoApprovals(savedNotesRef.current.noApprovals);
      }
      setEditing(false);
      setSaveErr("");
    }
    setActiveTab(tab);
  }

  async function save() {
    // Mandatory Details fields (John, 31 Jul 2026) — block save if any are blank.
    const missing = missingRequiredShelterFields(form);
    if (missing.length) {
      setActiveTab("details");
      setSaveErr(`Please complete the required field(s): ${missing.join(", ")}.`);
      return;
    }
    setSaving(true);
    setSaveErr("");
    const res = await updateShelterById(shelter.id, buildShelterPatch(form, user?.email));
    if (res.error) {
      setSaving(false);
      logger.warn("ShelterDetail save failed:", res.error.message);
      setSaveErr("Save failed — please try again.");
      return;
    }
    // db-127: bank details live in the admin-only shelter_bank_details table (upsert).
    const bankRes = await upsertBankDetails(shelter.id, bankForm, user?.email);
    setSaving(false);
    if (bankRes.error) {
      logger.warn("ShelterDetail bank save failed:", bankRes.error.message);
      setSaveErr("Save failed (bank details) — please try again.");
      return;
    }
    // DFV Service Notes (shelter_notes) — saved alongside the shelters patch. NOT synced
    // to Unleashed (separate table). Audit the contentious no-approvals flag on a flip only.
    const notesChanged =
      serviceNotes !== savedNotesRef.current.notes ||
      noApprovals !== savedNotesRef.current.noApprovals;
    if (notesChanged) {
      const notesRes = await upsertShelterNotes({
        shelter_code: shelter.shelter_code || null,
        shelter_name: shelter.shelter_name,
        notes: serviceNotes,
        no_approvals: noApprovals,
        updated_at: new Date().toISOString(),
      });
      if (notesRes.error) {
        logger.warn("ShelterDetail notes save failed:", notesRes.error.message);
        setSaveErr("Save failed (DFV Service Notes) — please try again.");
        return;
      }
      if (noApprovals !== savedNotesRef.current.noApprovals) {
        logAudit("shelter_no_approvals_changed", {
          shelter_code: shelter.shelter_code || null,
          shelter_name: shelter.shelter_name,
          enabled: noApprovals,
        });
      }
      savedNotesRef.current = { notes: serviceNotes, noApprovals };
    }
    logAudit("shelter_updated", {
      shelter_id: shelter.id,
      shelter_code: shelter.shelter_code || "",
      shelter_name: form.shelter_name || "",
      by: user?.email || "",
    });
    // Auto-sync (db-133): queue a Customer push so this edit reaches Unleashed. The
    // helper self-gates to Zephyr* codes; one customer job covers the customer +
    // bank + address fields (all folded into the Customer POST).
    // Capture what the user changed (portal-owned pushed fields) onto the job so the
    // Push List inspect modal can show "Address changed from X to Y", "Warehouse
    // changed", etc. Keep-first: if a job is already queued for this service, the enqueue
    // dedupe skips this one, retaining the FIRST job's changes (John, 4 Aug 2026).
    const changes = computeShelterChanges(shelter, form);
    void enqueueUnleashedPush({
      entityType: "customer",
      entityId: shelter.id,
      shelterCode: shelter.shelter_code,
      payload: changes.length ? { changes } : undefined,
    });
    savedFormRef.current = JSON.stringify(form);
    savedBankRef.current = bankForm;
    setEditing(false);
    onSaved(`DFV Service updated ${GLYPH.check}`);
  }

  // ── Primary user (db-41) ──────────────────────────────────────────────────
  const shelterMemberships = (memberships || []).filter(function (m) {
    return m.shelter_code === shelter.shelter_code;
  });
  // Contact-model 3c-iii (GOTCHA 3): the Users-tab rows show the per-service CONTACT
  // (name / email / phone / mobile / role) from the membership, not the profile's
  // legacy phones / primary_contact_label (which editUser no longer writes).
  const contactByUserId = {};
  shelterMemberships.forEach(function (m) {
    if (m.user_id) contactByUserId[m.user_id] = m;
  });
  // Contact-model Phase 2 (pointer-only): the Primary contact is whichever
  // membership shelters.primary_contact points at (is_primary_user is retired).
  const storedPrimary = shelterMemberships.find(function (m) {
    return m.id === shelter.primary_contact;
  });
  // Contact-model No-Login: rows are keyed by their user_shelters membership id —
  // shelters.primary_contact IS a membership id, and a login-less contact has no
  // user_id to key on. Resolve each row's membership (a login row via user_id; a
  // login-less row via its own membershipId) so the Primary selector works for both.
  const contactForRow = function (u) {
    return (
      (u.noLogin
        ? shelterMemberships.find(function (m) {
            return m.id === u.membershipId;
          })
        : contactByUserId[u.id]) || {}
    );
  };
  const membershipIdForRow = function (u) {
    return u.noLogin ? u.membershipId : contactForRow(u).id;
  };
  // While editing, the highlighted Primary is the DRAFT; otherwise it's the stored
  // pointer (shelters.primary_contact). No optimistic copy — after Save we reload.
  const storedPrimaryId = shelter.primary_contact || null;
  const primaryMembershipId = primaryEditing ? draftPrimary : storedPrimaryId;

  function startPrimaryEdit() {
    setDraftPrimary(storedPrimaryId);
    setPrimaryEditing(true);
  }
  function cancelPrimaryEdit() {
    setPrimaryEditing(false);
    setDraftPrimary(null);
  }
  function pickDraftPrimary(membershipId) {
    if (!primaryEditing || !membershipId) return;
    setDraftPrimary(membershipId);
  }
  async function savePrimary() {
    // Guard: a service must always keep a Primary — Save requires a pick.
    if (!draftPrimary || primarySaving) return;
    setPrimarySaving(true);
    // Point shelters.primary_contact straight at the chosen membership (works for a
    // login-less contact too — it has no user_id to resolve).
    const { error } = await setPrimaryContactMembership(shelter.id, draftPrimary);
    setPrimarySaving(false);
    if (error) {
      logger.warn("setPrimaryContactMembership failed:", error.message);
      return;
    }
    const chosen = shelterMemberships.find(function (m) {
      return m.id === draftPrimary;
    });
    logAudit("shelter_primary_user_set", {
      shelter_code: shelter.shelter_code || "",
      membership_id: draftPrimary,
      user_id: chosen ? chosen.user_id : null,
      by: user?.email || "",
    });
    // Queue the affected contacts so Unleashed's IsDefault re-points to match: the
    // newly-chosen primary → IsDefault true, and the previous primary → IsDefault
    // false. The pointer is already updated in the DB above, so each contact's
    // is_default is recomputed correctly at push time. Zephyr-gated + deduped inside
    // the helper; fire-and-forget, never blocks the save. The payload.changes note is
    // display-only (the Push List inspect modal shows "Primary contact: No → Yes").
    void enqueueContactPushForMembership(draftPrimary, "upsert", {
      changes: [{ label: "Primary contact", from: "No", to: "Yes" }],
    });
    if (storedPrimaryId && storedPrimaryId !== draftPrimary) {
      void enqueueContactPushForMembership(storedPrimaryId, "upsert", {
        changes: [{ label: "Primary contact", from: "Yes", to: "No" }],
      });
    }
    setPrimaryEditing(false);
    setDraftPrimary(null);
    onMembershipsChanged();
  }

  // Portal notes list (newest first), parsed from the JSON-in-text column; a legacy
  // free-text value degrades to one undated note.
  const portalNotes = parsePortalNotes(portalNotesRaw);
  // Add a portal note: write immediately (atomic action) + update the independent notes
  // state so the preview + modal refresh. Returns false on failure so the modal keeps the
  // draft. Not tied to the form Save, so the nav-guard is unaffected.
  async function addPortalNote(text) {
    if (notesBusy) return false;
    const next = prependNote(portalNotesRaw, { text, author: user?.email || null });
    setNotesBusy(true);
    const { error } = await updateShelterById(shelter.id, { dfv_service_portal_notes: next });
    setNotesBusy(false);
    if (error) {
      logger.warn("addPortalNote failed:", error.message);
      return false;
    }
    setPortalNotesRaw(next);
    logAudit("shelter_portal_note_added", {
      shelter_code: shelter.shelter_code || "",
      by: user?.email || "",
    });
    return true;
  }

  const users = attachedUsers || [];
  // The Primary contact always sorts to the top (John, 5 July 2026); the rest keep
  // their incoming order (Array.sort is stable).
  const sortedUsers = users
    .slice()
    // "Show Deactivated Contacts" tickbox (default off) hides deactivated rows.
    .filter((u) => showDeactivatedContacts || !u.deactivated)
    .sort(
      (a, b) =>
        (membershipIdForRow(a) === primaryMembershipId ? 0 : 1) -
        (membershipIdForRow(b) === primaryMembershipId ? 0 : 1)
    );
  // The user being edited (drives the edit MODAL below + the row highlight). A login
  // user matches by profile id; a login-less contact matches by membership id.
  const editingUserRow = !usersAdmin.editing
    ? null
    : usersAdmin.editing.table === "profiles"
      ? users.find(function (u) {
          return u.id === usersAdmin.editing.id;
        })
      : usersAdmin.editing.table === "nologin"
        ? users.find(function (u) {
            return u.noLogin && u.membershipId === usersAdmin.editing.membershipId;
          })
        : null;

  return (
    <div>
      {/* Back link + breadcrumb/title header are hidden in the telemarketing call
          modal — it already has its own teal header (service name + Close), so these
          would be a redundant second/third header (John, 9 Aug 2026). */}
      {!isTelemarketing && (
        <button
          type="button"
          onClick={handleBack}
          className="bg-transparent border-0 p-0 text-[13px] text-zephyr-teal cursor-pointer mb-[10px]"
          style={{ fontFamily: "inherit" }}
        >
          ← Back to DFV Services
        </button>
      )}

      <div className="bg-white border border-zephyr-border rounded-[12px] overflow-hidden">
        {/* Header (breadcrumb + title) — hidden in the telemarketing modal. */}
        {!isTelemarketing && (
          <div className="flex items-center gap-[14px] py-4 px-5 border-b border-zephyr-border flex-wrap">
            <div>
              <div className="text-[12px] text-zephyr-muted">
                DFV Services › <span className="font-semibold">{shelter.shelter_code}</span>
              </div>
              <span className="text-[24px] font-extrabold text-zephyr-teal-dark mr-[10px]">
                {shelter.shelter_code}
              </span>
              <span className="text-[15px] text-zephyr-muted">{form.shelter_name}</span>
              {shelter.deactivated && (
                <span className="ml-[8px] text-[10px] font-bold text-white bg-zephyr-red rounded-[3px] py-[1px] px-[6px] align-middle">
                  DEACTIVATED
                </span>
              )}
              {form.stop_credit && (
                <span
                  className="ml-[8px] text-[10px] font-bold text-white rounded-[3px] py-[1px] px-[6px] align-middle"
                  style={{ background: "#C62828" }}
                >
                  STOP CREDIT
                </span>
              )}
            </div>
          </div>
        )}

        {/* db-205 Sister services — above the tabs so it shows in BOTH the admin record and
            the telemarketing call screen. Self-loading + fail-safe (renders nothing on error). */}
        <SisterServicesStrip shelterId={shelter.id} shelterCode={shelter.shelter_code} />

        {/* Tabs */}
        <div className="flex gap-[2px] px-4 border-b border-zephyr-border bg-[#F7F9F9]">
          {TABS.map(function (pair) {
            const active = activeTab === pair[0];
            return (
              <button
                key={pair[0]}
                type="button"
                onClick={function () {
                  requestTab(pair[0]);
                }}
                className={[
                  "py-3 px-[22px] text-[14px] font-semibold cursor-pointer border-0 bg-transparent",
                  active ? "text-zephyr-teal-dark bg-white" : "text-zephyr-muted",
                ].join(" ")}
                style={{
                  fontFamily: "inherit",
                  borderBottom: active ? "3px solid #00695C" : "3px solid transparent",
                }}
              >
                {pair[1]}
              </button>
            );
          })}
        </div>

        {/* ── DETAILS ── three fixed columns (John's ordering, 5 July 2026):
            1) identity/classification  2) numbers/contact  3) Notes + Reminder */}
        {activeTab === "details" && (
          <div className="py-[22px] px-6">
            <fieldset
              disabled={!editing}
              className="border-0 p-0 m-0"
              style={{ minInlineSize: "auto" }}
            >
              {/* Columns 1 & 2 reduced 25% (John, 2 Aug 2026); Notes/Reminder (col 3)
                  absorb the freed width. */}
              <div
                className="grid items-stretch"
                style={{ gridTemplateColumns: "0.75fr 0.75fr 1.5fr", columnGap: 24 }}
              >
                {/* Column 1 */}
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="sd-code" className={labelCls}>
                      Customer Code{" "}
                      <span className="normal-case font-normal text-zephyr-muted">(read-only)</span>
                    </label>
                    <input
                      id="sd-code"
                      value={shelter.shelter_code || ""}
                      disabled
                      className={inputCls}
                      style={{ fontFamily: "inherit", background: "#EEF3F2", color: "#607D79" }}
                    />
                  </div>
                  <Field
                    id="sd-name"
                    label="Customer Name"
                    req
                    value={form.shelter_name}
                    onChange={(v) => setF("shelter_name", v)}
                  />
                  <SelectField
                    id="sd-customer-type"
                    label="DFV Service Type/Customer Type"
                    req
                    value={form.customer_type}
                    onChange={(v) => setF("customer_type", v)}
                    options={customerTypes}
                    allowEmpty
                  />
                  <SelectField
                    id="sd-state"
                    label="State"
                    req
                    value={form.state}
                    onChange={(v) => setF("state", v)}
                    options={STATES}
                    allowEmpty
                  />
                  <SelectField
                    id="sd-sales-group"
                    label="Sales Order Group"
                    req
                    value={form.sales_group}
                    onChange={(v) => setF("sales_group", v)}
                    options={salesGroups}
                    allowEmpty
                  />
                  <SelectField
                    id="sd-default-warehouse"
                    label="Default Warehouse"
                    req
                    value={form.default_warehouse}
                    onChange={(v) => setF("default_warehouse", v)}
                    options={warehouses || []}
                    allowEmpty
                  />
                  <Field
                    id="sd-date-onboarded"
                    label="Date Onboarded"
                    req
                    type="date"
                    value={form.date_onboarded}
                    onChange={(v) => setF("date_onboarded", v)}
                  />
                  {!isTelemarketing && (
                    <div>
                      <label htmlFor="sd-unleashed-guid" className={labelCls}>
                        Unleashed GUID{" "}
                        <span className="normal-case font-normal text-zephyr-muted">
                          (read-only)
                        </span>
                      </label>
                      <input
                        id="sd-unleashed-guid"
                        value={form.unleashed_guid || ""}
                        disabled
                        readOnly
                        className={inputCls}
                        style={{ fontFamily: "inherit", background: "#EEF3F2", color: "#607D79" }}
                      />
                    </div>
                  )}
                </div>
                {/* Column 2 */}
                <div className="flex flex-col gap-4">
                  {/* ABN + Taxable share the top row (John, screen 2). */}
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <Field
                      id="sd-abn"
                      label="ABN"
                      value={form.abn}
                      onChange={(v) => setF("abn", v)}
                    />
                    <div>
                      <label htmlFor="sd-taxable" className={labelCls}>
                        Taxable
                        <span className="text-zephyr-red">&nbsp;*</span>
                      </label>
                      <div className="flex items-center gap-2 min-h-9">
                        <Toggle
                          id="sd-taxable"
                          checked={form.taxable !== false}
                          onChange={(v) => setF("taxable", v)}
                          disabled={!editing}
                          label="Taxable"
                        />
                        <span className="text-[13px] text-zephyr-text">
                          {form.taxable !== false ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* db-153: portal-only Stock Held slide switch (NOT synced to Unleashed). */}
                  <div>
                    <label htmlFor="sd-stock-held" className={labelCls}>
                      Stock Held
                    </label>
                    <div className="flex items-center gap-2 min-h-9">
                      <Toggle
                        id="sd-stock-held"
                        checked={!!form.stock_held}
                        onChange={(v) => setF("stock_held", v)}
                        disabled={!editing}
                        label="Stock Held"
                      />
                      <span className="text-[13px] text-zephyr-text">
                        {form.stock_held ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                  {/* db-127: bank details (admin-only table) — Field names mirror the
                      Unleashed Customer CSV headers ("Bank Name" / "Bank Account"). */}
                  <Field
                    id="sd-bank-name"
                    label="Bank Name"
                    value={bankForm.bank_name}
                    onChange={(v) => setBankForm((b) => Object.assign({}, b, { bank_name: v }))}
                  />
                  <Field
                    id="sd-bank-account"
                    label="Bank Account"
                    value={bankForm.bank_account}
                    onChange={(v) => setBankForm((b) => Object.assign({}, b, { bank_account: v }))}
                  />
                  <div>
                    <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1">
                      Contact Email
                    </div>
                    <div
                      className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-muted"
                      style={{ background: "var(--color-zephyr-teal-light)" }}
                    >
                      {storedPrimary?.email || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1">
                      Contact Phone
                    </div>
                    <div
                      className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-muted"
                      style={{ background: "var(--color-zephyr-teal-light)" }}
                    >
                      {storedPrimary?.phone ? formatPhone(storedPrimary.phone) : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1">
                      Contact Mobile
                    </div>
                    <div
                      className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-muted"
                      style={{ background: "var(--color-zephyr-teal-light)" }}
                    >
                      {storedPrimary?.mobile ? formatPhone(storedPrimary.mobile) : "—"}
                    </div>
                  </div>
                  <div className="text-[11px] text-zephyr-muted">
                    Email / Phone / Mobile are the service's <b>Primary contact</b> — edit on the
                    Users tab (read-only here).
                  </div>
                </div>
                {/* Column 3 — Notes EXPANDS to fill the column; Reminder sits at the
                    BOTTOM (John, 2 Aug 2026). Relies on the grid row being stretched
                    (items-stretch) so this column gets the full height to fill. */}
                <div className="flex flex-col gap-4 h-full">
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-1">
                      <span className={labelCls} style={{ marginBottom: 0 }}>
                        Notes
                      </span>
                      <span className="text-[11px] font-normal text-zephyr-muted normal-case">
                        use “🗒 Notes” below to add
                      </span>
                    </div>
                    <div
                      className="flex-1 overflow-y-auto rounded-[8px] border border-zephyr-border p-2 bg-white"
                      style={{ minHeight: 160, maxHeight: 420 }}
                    >
                      {portalNotes.length === 0 ? (
                        <div className="text-[13px] text-zephyr-muted italic">
                          No notes yet — use the “🗒 Notes” button below to add one. Portal notes
                          (e.g. telemarketing) — not synced to Unleashed.
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {portalNotes.map((n, i) => (
                            <div
                              key={i}
                              className="border-b border-zephyr-border last:border-b-0 pb-2 last:pb-0"
                            >
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-[11px] font-semibold text-zephyr-text">
                                  {n.author || "—"}
                                </span>
                                <span className="text-[11px] text-zephyr-muted">
                                  {n.at ? new Date(n.at).toLocaleString("en-AU") : ""}
                                </span>
                              </div>
                              <div className="text-[13px] text-zephyr-text whitespace-pre-wrap">
                                {n.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="sd-reminder" className={labelCls}>
                      Reminder{" "}
                      <span className="text-xs font-normal text-gray-500">
                        (from Unleashed — read-only)
                      </span>
                    </label>
                    {/* Reminder is GET-only on the Unleashed API, so it can't be pushed;
                        it's synced Unleashed → portal on each Customer push. Read-only so
                        users don't think an edit here will save (John, 3 Aug 2026). */}
                    <textarea
                      id="sd-reminder"
                      value={form.reminder}
                      readOnly
                      rows={4}
                      className={inputCls}
                      style={{
                        resize: "vertical",
                        fontFamily: "inherit",
                        backgroundColor: "#f3f4f6",
                        cursor: "default",
                      }}
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        )}

        {/* ── ADDRESS ── Physical (left) | Postal (right), side by side */}
        {activeTab === "address" && (
          <div className="py-[22px] px-6">
            <fieldset
              disabled={!editing}
              className="border-0 p-0 m-0"
              style={{ minInlineSize: "auto" }}
            >
              <div className="grid grid-cols-2 items-start" style={{ columnGap: 32 }}>
                <div>
                  <div className="text-[12px] font-bold text-zephyr-teal-dark uppercase tracking-[0.5px] mb-3 pb-[6px] border-b border-zephyr-border">
                    Physical Address
                  </div>
                  <AddressBlock prefix="physical" form={form} setF={setF} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3 pb-[6px] border-b border-zephyr-border">
                    <span className="text-[12px] font-bold text-zephyr-teal-dark uppercase tracking-[0.5px]">
                      Postal Address
                    </span>
                    <button
                      type="button"
                      onClick={copyPhysicalToPostal}
                      className="bg-transparent border-0 p-0 text-[12px] text-zephyr-teal cursor-pointer"
                      style={{ fontFamily: "inherit" }}
                    >
                      ⧉ Copy from physical address
                    </button>
                  </div>
                  <AddressBlock prefix="postal" form={form} setF={setF} />
                </div>
              </div>
            </fieldset>
          </div>
        )}

        {/* ── USERS ── full management (Phase 3): new / attach / edit / detach /
            deactivate, plus the Primary selector. The edit form expands inline
            under the row (UserAdmin owns editing/form via usersAdmin). */}
        {activeTab === "users" && (
          <div className="py-[22px] px-6">
            <div className="flex items-center justify-between gap-2 mb-3 pb-[6px] border-b border-zephyr-border flex-wrap">
              <span className="text-[12px] font-bold text-zephyr-teal-dark uppercase tracking-[0.5px]">
                Users{" "}
                <span className="normal-case font-normal text-zephyr-muted tracking-normal">
                  — the people attached to this DFV Service
                </span>
              </span>
              <span className="flex items-center gap-2 flex-wrap">
                {/* Primary contact is a staged edit: read-only until "Change Primary",
                    then pick a radio + Save (a service must always keep a Primary, so
                    Save is disabled until one is picked). */}
                {primaryEditing ? (
                  <>
                    <span className="text-[11px] text-zephyr-muted">
                      Select the Primary contact below →
                    </span>
                    <button
                      type="button"
                      onClick={savePrimary}
                      disabled={!draftPrimary || primarySaving}
                      className="bg-zephyr-teal text-white rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer font-semibold disabled:opacity-50"
                      style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                    >
                      {primarySaving ? "Saving..." : "Save Primary"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelPrimaryEdit}
                      disabled={primarySaving}
                      className="bg-transparent rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer text-zephyr-muted"
                      style={{ border: "1px solid #CFE0DE", fontFamily: "inherit" }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startPrimaryEdit}
                    disabled={users.length === 0}
                    className="bg-transparent rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer text-zephyr-teal-dark font-semibold disabled:opacity-50"
                    style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                  >
                    ◎ Change Primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={function () {
                    setShowAttachModal(true);
                  }}
                  className="bg-transparent rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer text-zephyr-teal-dark font-semibold"
                  style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                >
                  ⚲ Attach existing user
                </button>
                <button
                  type="button"
                  onClick={usersAdmin.onNewUser}
                  className="bg-zephyr-teal-light rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer text-zephyr-teal-dark font-semibold"
                  style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                >
                  + New User
                </button>
              </span>
            </div>
            {/* Show Deactivated Contacts filter (default off — John, screen 1). */}
            <label className="flex items-center gap-2 text-[12px] text-zephyr-muted mb-3 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={showDeactivatedContacts}
                onChange={(e) => setShowDeactivatedContacts(e.target.checked)}
              />
              Show Deactivated Contacts
            </label>
            {users.length === 0 ? (
              <div className="text-[13px] text-zephyr-muted italic">No users attached.</div>
            ) : (
              <div className="border border-zephyr-border rounded-[8px] overflow-hidden">
                <div
                  className="grid items-center py-[10px] px-[14px] bg-zephyr-teal-dark text-white text-[11px] font-bold uppercase tracking-[0.5px]"
                  style={{
                    gridTemplateColumns: "70px 1.3fr 1fr 1.4fr 110px 70px 110px 90px 220px",
                  }}
                >
                  <div>Primary</div>
                  <div>Name</div>
                  <div>Role</div>
                  <div>Email</div>
                  <div>Phone/Mobile</div>
                  <div>Login</div>
                  <div>Account Type</div>
                  <div>Status</div>
                  <div>Actions</div>
                </div>
                {sortedUsers.map(function (u) {
                  const isEditingUser = editingUserRow && editingUserRow.id === u.id; // row highlight only
                  // Per-service contact for this row's membership (login OR login-less).
                  const contact = contactForRow(u);
                  const rowMembershipId = membershipIdForRow(u);
                  // THE WALL: this list is the service's CONTACTS, so the name comes
                  // from the membership. The old profiles.staff_* fallback is gone —
                  // that is Zephyr staff only (docs/staff-caseworker-separation-plan.md).
                  const contactName =
                    [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
                    u.display_name ||
                    contact.email ||
                    u.id;
                  const contactEmail = contact.email || u.staff_user_email || "—";
                  return (
                    <div key={u.id} className="border-t border-[#EEF3F2]">
                      <div
                        className="grid items-center py-[10px] px-[14px] text-[13px]"
                        style={{
                          gridTemplateColumns: "70px 1.3fr 1fr 1.4fr 110px 70px 110px 90px 220px",
                          background: isEditingUser ? "#F0F7F6" : "transparent",
                        }}
                      >
                        <div>
                          <input
                            type="radio"
                            name="sd-primary-user"
                            aria-label={`Primary: ${contactName}`}
                            checked={!!rowMembershipId && primaryMembershipId === rowMembershipId}
                            disabled={!primaryEditing || !!u.deactivated || !rowMembershipId}
                            onChange={function () {
                              pickDraftPrimary(rowMembershipId);
                            }}
                          />
                        </div>
                        <div>
                          <div className="font-semibold">{contactName}</div>
                        </div>
                        {/* Role column shows the per-service CONTACT ROLE (what the
                            admin assigns), not the account login type (which is
                            "Caseworker" for every DFV-service user — redundant here). */}
                        <div className="text-zephyr-muted">{contact.contact_label || "—"}</div>
                        <div className="text-zephyr-muted break-words">{contactEmail}</div>
                        {/* Phone on top, mobile stacked beneath (John, 27 Aug 2026). */}
                        <div className="text-zephyr-muted">
                          {contact.phone && <div>{formatPhone(contact.phone)}</div>}
                          {contact.mobile && <div>{formatPhone(contact.mobile)}</div>}
                          {!contact.phone && !contact.mobile && "—"}
                        </div>
                        {/* Login = does this person have a login account (vs a
                            contact-only record). Separate from the Contact Role. */}
                        <div className={u.noLogin ? "text-zephyr-muted" : "font-semibold"}>
                          {u.noLogin ? "No" : "Yes"}
                        </div>
                        {/* Account Type (John, 8 Aug): the two edit-modal tickboxes surfaced
                            on the list — Shared/generic, Contact-only, or a normal User. */}
                        <div>
                          {u.noLogin ? (
                            <span className="text-[10px] font-bold text-white bg-[#607D8B] rounded-[3px] py-[1px] px-[6px]">
                              CONTACT ONLY
                            </span>
                          ) : u.is_generic ? (
                            <span className="text-[10px] font-bold text-white bg-zephyr-teal rounded-[3px] py-[1px] px-[6px]">
                              SHARED
                            </span>
                          ) : (
                            <span className="text-zephyr-muted">User</span>
                          )}
                        </div>
                        <div>
                          {/* STATUS applies to BOTH login users (profiles.deactivated) and
                              login-less contacts (user_shelters.deactivated, db-189). */}
                          {u.deactivated ? (
                            <span className="text-[10px] font-bold text-white bg-zephyr-red rounded-[3px] py-[1px] px-[5px]">
                              DEACTIVATED
                            </span>
                          ) : (
                            <div>
                              <div>Active</div>
                              {!u.noLogin && hasLoggedIn(loginActivation, u.id) && (
                                <span
                                  className="text-[10px] font-bold text-white bg-[#2E7D32] rounded-[3px] py-[1px] px-[5px] mt-[3px] inline-block"
                                  title="This login has been used at least once"
                                >
                                  Live
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-[5px]">
                          {/* Edit works for both a login user (profile + membership)
                              and a login-less contact (membership only) — startEdit
                              detects u.noLogin. */}
                          <button
                            type="button"
                            onClick={function () {
                              // Thread this service's id (contact-model 3c-iii,
                              // GOTCHA 2) so the per-service Contact Role + fields
                              // seed from / save to the RIGHT membership.
                              usersAdmin.startEdit(u, "profiles", shelter.id);
                            }}
                            className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer"
                            style={{
                              border: "1px solid #CFE0DE",
                              color: "#1A2E2B",
                              fontFamily: "inherit",
                            }}
                          >
                            Edit
                          </button>
                          {/* Remove = detach a LOGIN user from this service (keeps their
                              account + other services). Login-less contacts are NOT removed
                              here — they're deactivated via the button below (db-189). */}
                          {!u.noLogin && (
                            <button
                              type="button"
                              onClick={function () {
                                usersAdmin.onDetachUser(u);
                              }}
                              className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer"
                              style={{
                                border: "1px solid #CFE0DE",
                                color: "#1A2E2B",
                                fontFamily: "inherit",
                              }}
                            >
                              Remove
                            </button>
                          )}
                          {/* Deactivate / Reactivate. Login user → profile/auth action.
                              Login-less contact (db-189) → soft-delete the membership (keep
                              the row, null the GUID, delete the Unleashed contact) or
                              reactivate it (new GUID). The deactivate is BLOCKED if the
                              contact is the service's Primary (handler + DB trigger). */}
                          <button
                            type="button"
                            onClick={function () {
                              if (u.noLogin) {
                                if (u.deactivated) usersAdmin.onReactivateNoLogin(u);
                                else usersAdmin.onRemoveNoLogin(u);
                              } else {
                                usersAdmin.setConfirmDeactivate(u);
                              }
                            }}
                            className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer"
                            style={{
                              border: `1px solid ${u.deactivated ? "#2E7D32" : "#EF5350"}`,
                              color: u.deactivated ? "#2E7D32" : "#C62828",
                              fontFamily: "inherit",
                            }}
                          >
                            {u.deactivated ? "Reactivate" : "Deactivate"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* User edit MODAL (John's call, 5 July 2026: a modal is clearer
                than the first-cut inline expand-under-the-row). */}
            {editingUserRow && (
              <ModalBackdrop
                onClose={usersAdmin.cancelEdit}
                portal
                draggable
                dragTitle={
                  usersAdmin.editing?.table === "nologin"
                    ? "Editing Contact (no login) — drag to move"
                    : "Editing User — drag to move"
                }
                contentStyle={{
                  background: "#FFFFFF",
                  borderRadius: 14,
                  width: "calc(100vw - 40px)",
                  maxWidth: 920,
                  maxHeight: "calc(100vh - 60px)",
                  overflowY: "auto",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
                }}
              >
                <CaseworkerEditForm
                  user={editingUserRow}
                  form={usersAdmin.form}
                  setForm={usersAdmin.setForm}
                  fv={usersAdmin.fv}
                  setF={usersAdmin.setF}
                  saving={usersAdmin.saving}
                  saveEdit={usersAdmin.saveEdit}
                  sendPasswordReset={usersAdmin.sendPasswordReset}
                  shelters={usersAdmin.shelters}
                  serviceLabel={`${shelter.shelter_name} (${shelter.shelter_code})`}
                  noLoginContact={usersAdmin.editing?.table === "nologin"}
                  contactGuid={contactForRow(editingUserRow)?.unleashed_contact_guid || null}
                  onCancel={function () {
                    usersAdmin.cancelEdit();
                  }}
                />
              </ModalBackdrop>
            )}
            {users.length > 0 && !primaryMembershipId && (
              <div className="mt-[10px] text-[12px] font-semibold" style={{ color: "#B8860B" }}>
                {primaryEditing
                  ? "⚠️ Select the Primary contact above, then Save Primary."
                  : "⚠️ This DFV Service has no Primary contact — click “Change Primary” to set one."}
              </div>
            )}
            <AttachUserModal
              open={showAttachModal}
              shelterName={form.shelter_name}
              candidates={(usersAdmin.caseworkers || [])
                .filter(function (c) {
                  return (
                    !c.deactivated &&
                    !users.some(function (u) {
                      return u.id === c.id;
                    })
                  );
                })
                // THE WALL: show each candidate by their membership CONTACT name,
                // not profiles.staff_* (Zephyr staff only). The person-level name is
                // the same across all their memberships, so any row with a name
                // serves. display_name remains the fallback in the modal.
                .map(function (c) {
                  const contact = (memberships || []).find(function (m) {
                    return m.user_id === c.id && (m.first_name || m.last_name || m.email);
                  });
                  return contact
                    ? {
                        ...c,
                        first_name: contact.first_name,
                        last_name: contact.last_name,
                        email: contact.email,
                      }
                    : c;
                })}
              onAttach={function (userId) {
                setShowAttachModal(false);
                usersAdmin.onAttachUser(userId);
              }}
              onClose={function () {
                setShowAttachModal(false);
              }}
            />
            <div className="mt-[10px] text-[12px] text-zephyr-muted">
              Exactly one contact per DFV Service is Primary. Click “Change Primary”, pick one, then
              Save Primary.
            </div>
            <div className="mt-[6px] text-[12px] font-bold text-zephyr-text">
              NOTE — Deactivating a contact here DELETES it in Unleashed. Reactivating creates a new
              contact with a new Unleashed ID.
            </div>
          </div>
        )}

        {/* ── DELIVERY ── (inline management; no more Manage-Addresses modal) */}
        {activeTab === "delivery" && (
          <div className="py-[22px] px-6">
            <div className="flex items-center justify-between mb-3 pb-[6px] border-b border-zephyr-border">
              <span className="text-[12px] font-bold text-zephyr-teal-dark uppercase tracking-[0.5px]">
                Delivery Addresses
              </span>
              <button
                type="button"
                onClick={startNewAddr}
                className="bg-zephyr-teal-light rounded-[7px] py-[6px] px-[14px] text-[12px] cursor-pointer text-zephyr-teal-dark font-semibold"
                style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
              >
                + Add Address
              </button>
            </div>
            {deliveryLoading ? (
              <div className="text-[13px] text-zephyr-muted">Loading...</div>
            ) : (
              <DeliveryAddressRows deliveryAddrs={deliveryAddrs} startEditAddr={startEditAddr} />
            )}
            <div className="mt-[10px] text-[12px] font-bold text-zephyr-text">
              NOTE — Deactivating, banning or silencing an address removes it from this service’s
              Unleashed delivery addresses — unless it is the last active one, which Unleashed
              keeps.
            </div>
            {editingAddr !== null && (
              <DeliveryAddressEditModal
                isNew={editingAddr === "new"}
                addrForm={addrForm}
                setAddrForm={setAddrForm}
                deliverySaving={deliverySaving}
                wasDefault={editingAddr !== "new" && !!deliveryAddrs[editingAddr]?.is_default}
                onSave={saveAddr}
                onClose={function () {
                  setEditingAddr(null);
                }}
              />
            )}
          </div>
        )}

        {/* DFV Service Notes (shelter_notes) — the responder-facing note + the audited
            "no requests to approve — refer to management" flag. Behind Edit; the checkbox
            sits IN the blue bar (white); saved by the ONE Save below (no separate button).
            ⚠️ NOT synced to Unleashed — shelter_notes is a portal-internal table. */}
        {activeTab === "details" && (
          <div className="px-6 pb-4">
            <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
              <div
                className="text-white px-[18px] py-2 font-bold text-sm flex items-center justify-between gap-3 flex-wrap"
                style={{ background: noApprovals ? "#C62828" : "var(--color-zephyr-blue)" }}
              >
                <span>DFV Service Notes (Responders Only)</span>
                {isAdminRole(user) ? (
                  <label className="flex items-center gap-2 font-normal text-[12px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={noApprovals}
                      disabled={!editing}
                      onChange={(e) => setNoApprovals(e.target.checked)}
                    />
                    No requests for this shelter to be approved — refer to management
                  </label>
                ) : (
                  noApprovals && (
                    <span className="font-normal text-[12px]">
                      ⛔ No requests to approve — refer to management
                    </span>
                  )
                )}
              </div>
              <div className="p-[14px]">
                <textarea
                  value={serviceNotes}
                  onChange={(e) => setServiceNotes(e.target.value)}
                  disabled={!editing}
                  placeholder={`Internal notes about ${shelter.shelter_name || "this DFV Service"}…`}
                  rows={3}
                  className="w-full py-2 px-[10px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text box-border resize-y disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Prepared top-ups (19 Oct 2026) — staff see + cancel top-ups prepared for this
            service. Same audience as the Raise Stock Top-up button. Hidden when none. */}
        {activeTab === "details" && canRaiseTopUp && (
          <div className="px-6 pb-4 empty:hidden">
            <PreparedTopUpsPanel
              shelterId={shelter.id}
              shelterCode={shelter.shelter_code}
              reloadKey={prefillReload}
            />
          </div>
        )}

        {/* Footer actions — Details + Address tabs only. The six action buttons
            (Notes / Unleashed Notes / Documents / History / View Requests +
            Deactivate) are Details-tab only (John, 9 Aug 2026). The ONE Save lives
            here too. */}
        {(activeTab === "details" || activeTab === "address") && (
          <div className="flex gap-[10px] items-center py-4 px-6 border-t border-zephyr-border">
            {/* Stop Credit (db-127) — Details tab only; read-only until Edit. Saved via
                buildShelterPatch. Portal doesn't gate on it; it's pushed to Unleashed. */}
            {activeTab === "details" && (
              <div className="flex items-center gap-2 mr-2">
                <span className="text-[11px] font-bold text-zephyr-muted uppercase tracking-[0.4px]">
                  Stop Credit
                </span>
                <Toggle
                  id="sd-stop-credit"
                  checked={!!form.stop_credit}
                  onChange={(v) => setF("stop_credit", v)}
                  disabled={!editing}
                  label="Stop Credit"
                  onColor="#C62828"
                />
              </div>
            )}
            {editing ? (
              <>
                <Btn onClick={save} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Btn>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="rounded-[8px] py-[7px] px-4 text-[14px] cursor-pointer font-semibold bg-transparent"
                  style={{ border: "1px solid #607D79", color: "#607D79", fontFamily: "inherit" }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <Btn onClick={startEdit}>Edit</Btn>
            )}
            {saveErr && (
              <span className="text-[13px] text-zephyr-red font-semibold">{saveErr}</span>
            )}
            {activeTab === "details" &&
              !isTelemarketing &&
              (shelter.deactivated ? (
                <button
                  type="button"
                  onClick={function () {
                    onReactivateShelter(shelter);
                  }}
                  className="rounded-[8px] py-[7px] px-4 text-[14px] cursor-pointer font-semibold bg-transparent"
                  style={{ border: "1px solid #00897B", color: "#00695C", fontFamily: "inherit" }}
                >
                  Reactivate DFV Service
                </button>
              ) : (
                <button
                  type="button"
                  onClick={function () {
                    onDeactivateShelter(shelter);
                  }}
                  className="rounded-[8px] py-[7px] px-4 text-[14px] cursor-pointer font-semibold bg-transparent"
                  style={{ border: "1px solid #C62828", color: "#C62828", fontFamily: "inherit" }}
                >
                  Deactivate DFV Service
                </button>
              ))}
            <div className="flex-1" />
            {activeTab === "details" && (
              <>
                <button
                  type="button"
                  onClick={() => setNotesOpen(true)}
                  className="bg-zephyr-teal rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-white"
                  style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                >
                  🗒 Notes
                </button>
                <button
                  type="button"
                  onClick={() => setUnleashedNotesOpen(true)}
                  className="bg-transparent rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
                  style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                >
                  🗒 Unleashed Notes
                </button>
                <button
                  type="button"
                  onClick={openDocs}
                  className="bg-transparent rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
                  style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                >
                  📄 Documents{docs.length ? ` (${docs.length})` : ""}
                </button>
                <button
                  type="button"
                  onClick={openHistory}
                  className="bg-transparent rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
                  style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                >
                  🕘 History
                </button>
                {!isTelemarketing && (
                  <button
                    type="button"
                    onClick={function () {
                      navigate("/admin", { state: { shelterFilter: shelter.shelter_name } });
                    }}
                    className="bg-transparent rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
                    style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                  >
                    🔎 View Requests
                  </button>
                )}
                {canRaiseTopUp && (
                  <button
                    type="button"
                    onClick={() => setRaiseTopUpOpen(true)}
                    className="bg-transparent rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
                    style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                  >
                    📦 Raise Stock Top-up
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Change-history modal (db-127) — who changed what, when. Read-only. */}
      {historyOpen && (
        <ModalBackdrop onClose={() => setHistoryOpen(false)} portal>
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 14,
              width: "calc(100vw - 40px)",
              maxWidth: 760,
              maxHeight: "calc(100vh - 60px)",
              overflowY: "auto",
              boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div className="flex items-center justify-between py-3 px-5 border-b border-zephyr-border">
              <span className="text-[15px] font-bold text-zephyr-teal-dark">
                Change History — {shelter.shelter_code}
              </span>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                aria-label="Close"
                className="bg-transparent border-0 text-[22px] leading-none cursor-pointer text-zephyr-muted"
                style={{ fontFamily: "inherit" }}
              >
                ×
              </button>
            </div>
            <div className="p-5">
              {historyLoading ? (
                <div className="text-[13px] text-zephyr-muted">Loading…</div>
              ) : history.length === 0 ? (
                <div className="text-[13px] text-zephyr-muted italic">No changes recorded yet.</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {history.map(function (h) {
                    return (
                      <div key={h.id} className="border border-zephyr-border rounded-[8px] p-3">
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                          <span className="text-[13px] font-semibold text-zephyr-text">
                            {h.actorName}
                            {h.sourceTable !== "shelters" && (
                              <span className="ml-2 text-[10px] font-bold text-zephyr-muted uppercase">
                                {h.sourceTable === "shelter_bank_details" ? "bank" : h.sourceTable}
                              </span>
                            )}
                          </span>
                          <span className="text-[12px] text-zephyr-muted">
                            {new Date(h.changedAt).toLocaleString("en-AU")}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          {Object.entries(h.changes).map(function (entry) {
                            const field = entry[0];
                            const d = entry[1];
                            return (
                              <div key={field} className="text-[12px]">
                                <span className="font-semibold text-zephyr-muted">{field}</span>:{" "}
                                <span className="text-zephyr-red line-through">
                                  {fmtChangeValue(d.old)}
                                </span>{" "}
                                →{" "}
                                <span className="text-zephyr-teal-dark">
                                  {fmtChangeValue(d.new)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Unleashed Notes modal — READ-ONLY + draggable. Unleashed's Notes are imported
          once then not used (carried live on the Customer push); shown here for reference. */}
      {unleashedNotesOpen && (
        <ModalBackdrop
          onClose={() => setUnleashedNotesOpen(false)}
          portal
          draggable
          dragTitle={`Unleashed Notes — ${shelter.shelter_code}`}
          contentStyle={{
            background: "#FFFFFF",
            borderRadius: 14,
            width: "calc(100vw - 40px)",
            maxWidth: 640,
            maxHeight: "calc(100vh - 60px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
          }}
        >
          <div className="p-5 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
            {form.internal_notes ? (
              <div
                className="text-[13px] text-zephyr-text whitespace-pre-wrap"
                style={{ fontFamily: "inherit" }}
              >
                {form.internal_notes}
              </div>
            ) : (
              <div className="text-[13px] text-zephyr-muted italic">
                No Unleashed notes for this service.
              </div>
            )}
          </div>
        </ModalBackdrop>
      )}

      {/* Portal notes modal (db-130) — add a note (top) + notes on file (newest first,
          below). Draggable. Notes write immediately; no templates. */}
      {notesOpen && (
        <ShelterPortalNotesModal
          shelterCode={shelter.shelter_code}
          notes={portalNotes}
          onAddNote={addPortalNote}
          busy={notesBusy}
          onClose={() => setNotesOpen(false)}
        />
      )}

      {/* Phase 5 — staff "Raise Stock Top-up": prepare a top-up FOR this service. */}
      {raiseTopUpOpen && (
        <RaiseTopUpModal
          shelterId={shelter.id}
          shelterCode={shelter.shelter_code}
          shelterName={shelter.shelter_name}
          primaryEmail={storedPrimary?.email || null}
          preparedBy={user?.id ?? null}
          onSaved={() => setPrefillReload((n) => n + 1)}
          onClose={() => setRaiseTopUpOpen(false)}
        />
      )}

      {/* Documents modal (db-128) — admin-only PDF upload + open. No remove. */}
      {docsOpen && (
        <ModalBackdrop onClose={() => setDocsOpen(false)} portal>
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 14,
              width: "calc(100vw - 40px)",
              maxWidth: 640,
              maxHeight: "calc(100vh - 60px)",
              overflowY: "auto",
              boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div className="flex items-center justify-between py-3 px-5 border-b border-zephyr-border">
              <span className="text-[15px] font-bold text-zephyr-teal-dark">
                Documents — {shelter.shelter_code}
              </span>
              <button
                type="button"
                onClick={() => setDocsOpen(false)}
                aria-label="Close"
                className="bg-transparent border-0 text-[22px] leading-none cursor-pointer text-zephyr-muted"
                style={{ fontFamily: "inherit" }}
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <label
                  className="bg-zephyr-teal-light rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
                  style={{ border: "1px solid #00897B" }}
                >
                  {docUploading ? "Uploading…" : "＋ Upload PDF"}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={onUploadDoc}
                    disabled={docUploading}
                    className="hidden"
                  />
                </label>
                <span className="text-[12px] text-zephyr-muted">PDF files only.</span>
              </div>
              {docErr && (
                <div className="text-[13px] text-zephyr-red font-semibold mb-2">{docErr}</div>
              )}
              {docsLoading ? (
                <div className="text-[13px] text-zephyr-muted">Loading…</div>
              ) : docs.length === 0 ? (
                <div className="text-[13px] text-zephyr-muted italic">
                  No documents uploaded yet.
                </div>
              ) : (
                <div className="border border-zephyr-border rounded-[8px] overflow-hidden">
                  {docs.map(function (d, di) {
                    return (
                      <div
                        key={d.path}
                        className="flex items-center justify-between gap-2 py-[10px] px-[14px] text-[13px]"
                        style={{ borderTop: di > 0 ? "1px solid #EEF3F2" : "none" }}
                      >
                        <span className="text-zephyr-text overflow-hidden text-ellipsis whitespace-nowrap">
                          📄 {docDisplayName(d.name)}
                        </span>
                        <button
                          type="button"
                          onClick={() => openDoc(d.path)}
                          className="bg-transparent rounded-[6px] py-1 px-[12px] text-[12px] cursor-pointer font-semibold text-zephyr-teal-dark shrink-0"
                          style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                        >
                          Open
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
